import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import apiService from "../services/api";
import { useAISupportSSE, AISupportEvent } from "../hooks/useAISupportSSE";
import { buildEnvelope, ScreenSnapshot } from "../utils/clientContext";
import {
  AISupportLang,
  readStoredLang,
  storeLang,
  t,
} from "../i18n/aiSupport";
import {
  AISupportOutcome,
  AISupportPendingAction,
  AISupportSessionDetail,
  AISupportSessionSummary,
  AISupportStoredMessage,
} from "../types/api";
import { Walkthrough, WalkthroughStep } from "../components/AISupportWalkthrough";

export interface AISupportTurn {
  id: string;
  /** `support` is a human supporter answering, shown differently from the assistant. */
  role: "user" | "assistant" | "support";
  text: string;
  author?: string;
  streaming?: boolean;
}

interface AISupportContextValue {
  isOpen: boolean;
  lang: AISupportLang;
  sessionId: number | null;
  turns: AISupportTurn[];
  isBusy: boolean;
  activity: string | null;
  pendingAction: AISupportPendingAction | null;
  isEscalated: boolean;
  errorMessage: string | null;
  awaitingVerdict: boolean;
  walkthrough: Walkthrough | null;
  dismissWalkthrough: () => void;
  /** The last guide shown, kept so it can be reopened after being closed. */
  lastWalkthrough: Walkthrough | null;
  reopenWalkthrough: () => void;
  /** Past conversations, so a therapist can pick one back up. */
  history: AISupportSessionSummary[];
  historyFilter: "open" | "all";
  setHistoryFilter: (filter: "open" | "all") => void;
  isHistoryOpen: boolean;
  openHistory: () => void;
  closeHistory: () => void;
  resumeSession: (sessionId: number) => Promise<void>;
  /** Answers that arrived while the therapist was not looking. */
  unreadCount: number;
  open: () => void | Promise<void>;
  close: () => void;
  setLang: (lang: AISupportLang) => void;
  send: (text: string) => Promise<void>;
  escalate: () => Promise<void>;
  /**
   * Decide the pending action, when it is one this person may decide. Only ever
   * offered for `pendingAction.may_approve`; everything else waits for human
   * support, and pressing nothing is a valid thing to do.
   */
  decidePendingAction: (approve: boolean) => Promise<void>;
  /** True while that decision is in flight, so the buttons can be disabled. */
  isDeciding: boolean;
  /**
   * There is an open conversation that still needs a verdict. Drives the ask
   * shown when the therapist dismisses the panel: pressing the × does NOT
   * close the conversation, and without being told that they assume it does.
   */
  isClosable: boolean;
  /** The "how did it end?" ask is showing. Raised by dismissing the panel and
   *  by the sign-in reminder; answered by a verdict, or waved away. */
  isCloseAskOpen: boolean;
  openCloseAsk: () => void;
  dismissCloseAsk: () => void;
  /** `note` is whatever the therapist typed in their own words. It reaches the
   *  knowledge base, so it is the most useful thing they can leave behind. */
  submitVerdict: (outcome: AISupportOutcome, note?: string) => Promise<void>;
  /** The same writing, sent on its own -- it is usually thought of a moment
   *  after the verdict button was pressed. Returns false if it did not land. */
  sendFeedbackNote: (note: string) => Promise<boolean>;
  reset: () => void;
  /** Screens call this so the assistant knows what is on screen. */
  setScreenSnapshot: (snapshot: ScreenSnapshot) => void;
}

// Five minutes of silence. Long enough that it never lands mid-thought while
// somebody is reading an answer or typing a follow-up, short enough that they
// are still at the same screen and remember what they asked.
const IDLE_BEFORE_CLOSE_PROMPT_MS = 5 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 30 * 1000;

/**
 * Whether to ask the therapist to close the conversation and say how it went.
 *
 * Pure and exported so the conditions can be tested: every one of them is a
 * case where nudging would be wrong, and getting any of them backwards means
 * interrupting somebody mid-task or nagging about a conversation they already
 * finished.
 */
export function shouldPromptToClose(state: {
  sessionId: number | null;
  alreadyPrompted: boolean;
  outcomeGiven: boolean;
  isEscalated: boolean;
  hasPendingAction: boolean;
  isBusy: boolean;
  idleMs: number;
}): boolean {
  if (state.sessionId === null) return false;
  // Asked once per quiet spell; asking twice is nagging.
  if (state.alreadyPrompted) return false;
  // Already finished -- being told to close a closed conversation reads as the
  // app not having noticed.
  if (state.outcomeGiven) return false;
  // A person is dealing with it. The therapist is right to be waiting, and
  // closing it now would take it off support's list.
  if (state.isEscalated) return false;
  // Blocked on somebody approving an action, which is waiting, not idling.
  if (state.hasPendingAction) return false;
  // An answer is still being written.
  if (state.isBusy) return false;
  return state.idleMs >= IDLE_BEFORE_CLOSE_PROMPT_MS;
}

const AISupportContext = createContext<AISupportContextValue | null>(null);

export const useAISupport = (): AISupportContextValue => {
  const ctx = useContext(AISupportContext);
  if (!ctx) throw new Error("useAISupport must be used within an AISupportProvider");
  return ctx;
};

/**
 * Unread answers, shared by every tab this therapist has open.
 *
 * Two tabs of the same app are one person, so an answer read in one has been
 * read in both. Keeping this only in React state meant the idle tab counted
 * every reply for ever, since nothing there was ever "read". localStorage is
 * shared across tabs of an origin and its `storage` event fires in the *other*
 * tabs, which is exactly the signal needed.
 */
const UNREAD_KEY = "aiSupportUnread";

/** Enough of the token to notice a different user signed in, and no more. */
const tokenFingerprint = () => (localStorage.getItem("token") || "").slice(-12);

const readStoredUnread = (): number[] => {
  try {
    const raw = localStorage.getItem(UNREAD_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Conversations belonging to whoever was signed in before are not this
    // therapist's unread answers.
    if (parsed?.who !== tokenFingerprint()) return [];
    return Array.isArray(parsed.ids) ? parsed.ids.filter((n: unknown) => typeof n === "number") : [];
  } catch {
    return [];
  }
};

const writeStoredUnread = (ids: number[]) => {
  try {
    localStorage.setItem(UNREAD_KEY, JSON.stringify({ who: tokenFingerprint(), ids }));
  } catch {
    // A full or disabled store is not a reason to break the widget.
  }
};

let turnCounter = 0;
const nextId = () => `turn-${++turnCounter}`;

/**
 * Recover the most recent walkthrough from a stored transcript.
 *
 * A guide arrives as a live event, so it is lost if the panel was closed
 * while the assistant worked, if the connection dropped, or if the page
 * reloaded. The steps are in the transcript though -- the tool call that
 * produced them is stored like any other -- so a conversation can always be
 * reopened with its guide intact.
 */
const walkthroughFromMessages = (messages: AISupportStoredMessage[]): Walkthrough | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    for (const block of messages[i].blocks || []) {
      const b = block as { type?: string; name?: string; input?: Walkthrough };
      if (b.type === "tool_use" && b.name === "propose_walkthrough" && b.input?.steps?.length) {
        return { title: b.input.title || "", steps: b.input.steps };
      }
    }
  }
  return null;
};

/**
 * The guide a reopened conversation comes back with.
 *
 * The server rebuilds it from the checked tool result: labels re-pointed at
 * the interface as it is today, and the screen it started on kept. Reading
 * the transcript here is the fallback, and it only ever recovers the model's
 * own arguments -- the steps, with no idea where any of them happen.
 */
const walkthroughFromDetail = (detail: AISupportSessionDetail): Walkthrough | null => {
  const served = detail.walkthrough;
  if (served?.steps?.length) {
    return {
      title: served.title || "",
      steps: served.steps as unknown as WalkthroughStep[],
      starts_at: served.starts_at as Walkthrough["starts_at"],
    };
  }
  return walkthroughFromMessages(detail.messages);
};

export const AISupportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [lang, setLangState] = useState<AISupportLang>(readStoredLang);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [turns, setTurns] = useState<AISupportTurn[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [activity, setActivity] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<AISupportPendingAction | null>(null);
  const [isDeciding, setIsDeciding] = useState(false);
  const [isEscalated, setIsEscalated] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [awaitingVerdict, setAwaitingVerdict] = useState(false);
  // A conversation nobody finished. The verdict row is always on screen, but a
  // therapist who got their answer and went back to work never looks at it
  // again, so the session stays open forever and the feedback -- the only
  // signal that says whether the answer was any good -- is never given. After
  // IDLE_BEFORE_CLOSE_PROMPT_MS of nothing happening, ask once.
  const [outcomeGiven, setOutcomeGiven] = useState(false);
  const [closePrompted, setClosePrompted] = useState(false);
  const [isCloseAskOpen, setIsCloseAskOpen] = useState(false);
  const lastActivityRef = useRef(Date.now());
  // Validated server-side before it ever arrives, so a step here always names a
  // control that exists in the shipped interface.
  const [walkthrough, setWalkthrough] = useState<Walkthrough | null>(null);
  // Closing a guide should not throw it away: a therapist closes it to see the
  // screen properly, then wants it back.
  const [lastWalkthrough, setLastWalkthrough] = useState<Walkthrough | null>(null);
  const [history, setHistory] = useState<AISupportSessionSummary[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // Unfinished conversations are what a therapist comes back to, so that is the
  // default view; everything else is a lookup they ask for.
  const [historyFilter, setHistoryFilter] = useState<"open" | "all">("open");
  // Which conversations the unread answers belong to, oldest first, so the
  // badge can take the therapist to the right one rather than to the newest.
  const [unreadSessions, setUnreadSessions] = useState<number[]>(readStoredUnread);
  // Derived, never its own counter. As a separate integer it counted *events*
  // while this list counted conversations, so several answers in one
  // conversation read as several waiting conversations -- and the two drifted
  // apart whenever one was cleared without the other.
  const unreadCount = unreadSessions.length;

  // Mutable, not state: these must not re-render the widget on every keystroke
  // elsewhere in the app, and the streaming turn is rewritten many times a second.
  const screenRef = useRef<ScreenSnapshot>({});
  const streamingIdRef = useRef<string | null>(null);
  const loadHistoryRef = useRef<(() => Promise<void>) | null>(null);
  const resumeSessionRef = useRef<((id: number) => Promise<void>) | null>(null);
  const resetRef = useRef<(() => void) | null>(null);
  // Read inside the event handler, which is memoised and would otherwise close
  // over a stale value.
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  // Anything that means the conversation is still going. Called from the event
  // handler and from send(), so the clock measures silence rather than age --
  // a long exchange must not be interrupted just because it started ten
  // minutes ago.
  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);
  // Read inside the cross-tab storage listener, which is bound once.
  const sessionIdRef = useRef<number | null>(null);
  sessionIdRef.current = sessionId;

  const token = () => localStorage.getItem("token") || "";

  const setScreenSnapshot = useCallback((snapshot: ScreenSnapshot) => {
    screenRef.current = snapshot;
  }, []);

  const setLang = useCallback((next: AISupportLang) => {
    setLangState(next);
    storeLang(next);
  }, []);

  const handleEvent = useCallback((event: AISupportEvent) => {
    // Every event from the server is the conversation being alive. Reset before
    // the switch rather than case by case, so a new event type cannot silently
    // fail to count as activity and provoke a nudge mid-exchange.
    markActivity();
    switch (event.type) {
      case "token_delta": {
        const delta = event.text || "";
        setTurns((prev) => {
          const id = streamingIdRef.current;
          if (id) {
            return prev.map((t) => (t.id === id ? { ...t, text: t.text + delta } : t));
          }
          const newId = nextId();
          streamingIdRef.current = newId;
          return [...prev, { id: newId, role: "assistant", text: delta, streaming: true }];
        });
        setActivity(null);
        break;
      }
      case "tool_start":
        setActivity(event.tool || null);
        break;
      case "tool_end":
        setActivity(null);
        break;
      case "assistant_done": {
        const finalText = event.text || "";
        setTurns((prev) => {
          const id = streamingIdRef.current;
          if (id) {
            return prev.map((t) =>
              t.id === id ? { ...t, text: finalText || t.text, streaming: false } : t
            );
          }
          if (!finalText) return prev;
          // Reconcile rather than append. The event can reach us more than once
          // — a reconnect, a second subscription in development — and the
          // backend stores exactly one assistant turn, so a repeat must not
          // become a second identical bubble on screen.
          const last = prev[prev.length - 1];
          if (last && last.role === "assistant" && last.text === finalText) return prev;
          // No deltas arrived (a tool-only turn, or a dropped connection);
          // show the final text rather than nothing.
          return [...prev, { id: nextId(), role: "assistant", text: finalText }];
        });
        streamingIdRef.current = null;
        setIsBusy(false);
        setActivity(null);
        setAwaitingVerdict(true);
        break;
      }
      case "support_reply":
        setTurns((prev) => {
          const text = event.text || "";
          const last = prev[prev.length - 1];
          if (!text || (last && last.role === "support" && last.text === text)) return prev;
          return [...prev, { id: nextId(), role: "support", text, author: event.by }];
        });
        setIsBusy(false);
        setActivity(null);
        // "This was handed to human support" is only true until they answer.
        // Leaving it under their reply reads as though nobody has responded yet.
        setIsEscalated(false);
        break;
      case "status":
        // Authoritative, and it survives a reload -- unlike the local busy flag.
        setIsBusy(event.status === "working");
        // The server returns a session to "active" once the assistant has
        // answered, which is exactly when the hand-off is over: a supporter's
        // guidance produces an answer from the assistant rather than a reply of
        // their own, so without this the therapist kept reading "passed to human
        // support" underneath the answer it had already produced.
        if (event.status === "active") setIsEscalated(false);
        break;
      case "walkthrough":
        if (Array.isArray(event.steps) && event.steps.length) {
          const next = {
            title: event.title || "",
            steps: event.steps as WalkthroughStep[],
            // Where the guide begins. Dropped here once, and the overlay could
            // then only fall back to each step's own place -- so a guide opened
            // from the wrong screen highlighted nothing instead of offering to
            // take them to the right one.
            starts_at: event.starts_at as unknown as Walkthrough["starts_at"],
          };
          // Only take over the screen if the therapist is actually looking. An
          // overlay appearing over their work while the panel is collapsed is
          // an interruption, not help -- keep it for the resume chip instead.
          if (isOpenRef.current) setWalkthrough(next);
          setLastWalkthrough(next);
        }
        break;
      case "approval_required":
        setPendingAction({
          id: event.action_id as number,
          session_id: event.session_id,
          tool_use_id: "",
          action_name: event.action || "",
          arguments: (event.arguments as Record<string, unknown>) || null,
          status: "pending",
          requested_at: null,
          decided_by_user_id: null,
          decision_note: null,
          may_approve: Boolean(event.may_approve),
        });
        setIsBusy(false);
        setActivity(null);
        break;
      case "approval_decided":
        setPendingAction(null);
        setIsBusy(true);
        break;
      case "escalated":
        setIsEscalated(true);
        setIsBusy(false);
        break;
      case "error":
        setErrorMessage(event.message || null);
        setIsBusy(false);
        setActivity(null);
        streamingIdRef.current = null;
        break;
      default:
        break;
    }
  }, [markActivity]);

  // Stay subscribed whenever a conversation is open, not only while the panel is
  // showing: a diagnosis takes a while, and a therapist who collapses the widget
  // meanwhile should not lose the answer -- or the guide -- that arrives.
  // The view tells the server which of those two states we are in, so a
  // collapsed panel keeps receiving without counting as "live".
  useAISupportSSE(
    sessionId,
    sessionId !== null,
    handleEvent,
    isOpen ? "widget" : "background"
  );

  // Answers that land while the widget is closed, or while it is open on a
  // different conversation, arrive on the per-user notification stream instead.
  // useNotifications re-broadcasts them on the DOM.
  useEffect(() => {
    const onReply = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const isCurrent = detail.session_id === sessionId;
      if (isCurrent && isOpen) return; // already visible in the panel
      if (typeof detail.session_id === "number") {
        setUnreadSessions((prev) =>
          prev.includes(detail.session_id) ? prev : [...prev, detail.session_id]
        );
      }
      // Keep the list fresh so opening history shows the new activity.
      void loadHistoryRef.current?.();
    };
    window.addEventListener("ai_support_reply", onReply);
    return () => window.removeEventListener("ai_support_reply", onReply);
  }, [sessionId, isOpen]);

  // Publish this tab's unread set, and adopt another tab's.
  useEffect(() => {
    writeStoredUnread(unreadSessions);
  }, [unreadSessions]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== UNREAD_KEY) return;
      // Whatever the other tab thinks, a conversation open on this screen is
      // being read right now -- it was the other tab that could not know that.
      const incoming = readStoredUnread().filter(
        (id) => !(isOpenRef.current && id === sessionIdRef.current)
      );
      setUnreadSessions((prev) =>
        prev.length === incoming.length && prev.every((id, i) => id === incoming[i])
          ? prev
          : incoming
      );
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Reading a conversation marks it read everywhere, not just in this tab.
  useEffect(() => {
    if (!isOpen || sessionId === null) return;
    setUnreadSessions((prev) =>
      prev.includes(sessionId) ? prev.filter((id) => id !== sessionId) : prev
    );
  }, [isOpen, sessionId]);

  const ensureSession = useCallback(async (): Promise<number> => {
    if (sessionId) return sessionId;
    const result = await apiService.startAISupportSession({
      token: token(),
      lang,
      client_context: buildEnvelope(screenRef.current),
    });
    setSessionId(result.session_id);
    return result.session_id;
  }, [sessionId, lang]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isBusy) return;
      setErrorMessage(null);
      setAwaitingVerdict(false);
      // They are still working in here, so the nudge is not due -- and if one
      // was already shown, allow another after the next quiet spell.
      markActivity();
      setClosePrompted(false);
      setTurns((prev) => [...prev, { id: nextId(), role: "user", text: trimmed }]);
      setIsBusy(true);
      try {
        const id = await ensureSession();
        await apiService.sendAISupportMessage({
          token: token(),
          session_id: id,
          message: trimmed,
          // Rebuilt every message: the therapist may have navigated since the
          // conversation started, and new errors may have landed.
          client_context: buildEnvelope(screenRef.current),
        });
      } catch (error: any) {
        console.error("[AISupport] send failed", error);
        setErrorMessage(error?.response?.data?.detail || null);
        setIsBusy(false);
      }
    },
    [ensureSession, isBusy, markActivity]
  );

  const escalate = useCallback(async () => {
    if (!sessionId) return;
    try {
      await apiService.escalateAISupport(token(), sessionId);
      setIsEscalated(true);
    } catch (error) {
      console.error("[AISupport] escalate failed", error);
    }
  }, [sessionId]);

  const decidePendingAction = useCallback(
    async (approve: boolean) => {
      if (!pendingAction || !pendingAction.may_approve) return;
      setIsDeciding(true);
      try {
        await apiService.decideAISupportAction(
          token(), pendingAction.id, approve ? "approve" : "deny"
        );
        // The conversation carries on from the server: `approval_decided`
        // clears the card and puts the widget back into "working", so nothing
        // is cleared here -- doing it twice made the panel flicker empty
        // before the answer arrived.
      } catch (error) {
        console.error("[AISupport] decide action failed", error);
        setErrorMessage(t(lang, "approvalFailed"));
      } finally {
        setIsDeciding(false);
      }
    },
    [pendingAction, lang]
  );

  const sendFeedbackNote = useCallback(
    async (note: string): Promise<boolean> => {
      const text = (note || "").trim();
      if (!sessionId || !text) return false;
      try {
        await apiService.sendAISupportFeedbackNote(token(), sessionId, text);
        return true;
      } catch (error) {
        console.error("[AISupport] feedback note failed", error);
        return false;
      }
    },
    [sessionId]
  );

  const submitVerdict = useCallback(
    async (outcome: AISupportOutcome, note?: string) => {
      if (!sessionId) return;
      setAwaitingVerdict(false);
      // Finished, however it went. Stops the idle nudge for good on this
      // conversation -- being asked to close something you just closed reads
      // as the app not having noticed.
      setOutcomeGiven(true);
      setIsCloseAskOpen(false);
      try {
        await apiService.sendAISupportFeedback({
          token: token(),
          session_id: sessionId,
          outcome,
          note: (note || "").trim() || undefined,
        });
        if (outcome === "need_human") {
          setIsEscalated(true);
        } else {
          // "Solved" and "didn't help" both close the conversation, and a closed
          // conversation has no answer waiting in it. Without this the id sits
          // in the unread list until someone reopens the very conversation they
          // just finished -- which nobody does -- so the badge shows an answer
          // that does not exist.
          setUnreadSessions((prev) =>
            prev.includes(sessionId) ? prev.filter((id) => id !== sessionId) : prev
          );
        }
      } catch (error) {
        console.error("[AISupport] feedback failed", error);
      }
    },
    [sessionId]
  );

  const reset = useCallback(() => {
    // Starting a new conversation has to leave the history list, or the click
    // clears state the therapist cannot see and looks like nothing happened.
    setIsHistoryOpen(false);
    // Deliberately leaves unreadSessions alone: answers waiting in *other*
    // conversations are still waiting, and clearing the badge here loses the
    // only pointer the therapist has to them.
    setSessionId(null);
    setTurns([]);
    setPendingAction(null);
    setIsEscalated(false);
    setErrorMessage(null);
    setAwaitingVerdict(false);
    setOutcomeGiven(false);
    setClosePrompted(false);
    setIsCloseAskOpen(false);
    lastActivityRef.current = Date.now();
    setIsBusy(false);
    setActivity(null);
    setWalkthrough(null);
    setLastWalkthrough(null);
    streamingIdRef.current = null;
  }, []);

  const dismissWalkthrough = useCallback(() => setWalkthrough(null), []);

  const reopenWalkthrough = useCallback(
    () => setWalkthrough((current) => current ?? lastWalkthrough),
    [lastWalkthrough]
  );

  const loadHistory = useCallback(async () => {
    try {
      const res = await apiService.getMyAISupportSessions(token());
      setHistory(res.sessions);
      // The unread list is stored locally and only ever cleared by opening a
      // conversation, so it outlives the conversations themselves -- one closed
      // by human support, or on another device, leaves an id behind here. Trust
      // the server about what is closed. Only ids it reports as closed are
      // dropped: the list is capped, and an id merely missing from it may be an
      // older conversation rather than a finished one.
      const closed = new Set(
        res.sessions.filter((s) => s.status === "closed").map((s) => s.id)
      );
      if (closed.size) {
        setUnreadSessions((prev) => {
          const next = prev.filter((id) => !closed.has(id));
          return next.length === prev.length ? prev : next;
        });
      }
    } catch (error) {
      console.error("[AISupport] history failed", error);
    }
  }, []);

  loadHistoryRef.current = loadHistory;

  // The badge paints from localStorage on the first render, before anything has
  // been fetched, so a stale entry is visible until something else happens to
  // load the history. Check once on mount, and only when there is actually a
  // badge to justify the request.
  useEffect(() => {
    if (readStoredUnread().length) void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openHistory = useCallback(() => {
    setIsHistoryOpen(true);
    void loadHistory();
  }, [loadHistory]);

  // Ask once, when a conversation has gone quiet and nobody finished it.
  //
  // Client-side on purpose. It is a nudge about this browser tab, not part of
  // the conversation: it costs no model call, is never written to the stored
  // transcript, and so cannot come back as an assistant message when the
  // conversation is replayed or read by support. The cron that would otherwise
  // do this runs hourly, which cannot express five minutes anyway.
  useEffect(() => {
    if (sessionId === null) return;
    const timer = setInterval(() => {
      if (!shouldPromptToClose({
        sessionId,
        alreadyPrompted: closePrompted,
        outcomeGiven,
        isEscalated,
        hasPendingAction: pendingAction !== null,
        isBusy,
        idleMs: Date.now() - lastActivityRef.current,
      })) return;
      setClosePrompted(true);
      setTurns((prev) => [
        ...prev,
        { id: nextId(), role: "assistant", text: t(lang, "closePrompt") },
      ]);
      // If the panel is collapsed they will never see it, so leave the same
      // marker an arriving answer leaves -- it is the only route back.
      if (!isOpenRef.current) {
        setUnreadSessions((prev) =>
          prev.includes(sessionId) ? prev : [...prev, sessionId]
        );
      }
    }, IDLE_CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [sessionId, closePrompted, outcomeGiven, isEscalated, pendingAction, isBusy, lang]);

  const closeHistory = useCallback(() => setIsHistoryOpen(false), []);

  /** Reopen a past conversation, rebuilding its turns from the stored transcript. */
  const resumeSession = useCallback(async (id: number) => {
    setIsHistoryOpen(false);
    setErrorMessage(null);
    setAwaitingVerdict(false);
    // Reopening an old conversation restarts the clock: it is idle by
    // definition, and nudging the instant it loads would be absurd.
    setClosePrompted(false);
    lastActivityRef.current = Date.now();
    setWalkthrough(null);
    setLastWalkthrough(null);
    streamingIdRef.current = null;
    try {
      const detail = await apiService.getAISupportSession(token(), id);
      setTurns(
        detail.messages
          .map((m: AISupportStoredMessage) => {
            const text = m.text || "";
            if (!text) return null;
            // Tool calls and their results are the assistant's working, not
            // conversation -- the therapist saw prose, so replay prose.
            if (m.role === "user" || m.role === "assistant" || m.role === "support_reply") {
              return {
                id: `stored-${m.id}`,
                role: m.role === "support_reply" ? ("support" as const)
                  : (m.role as "user" | "assistant"),
                text,
              };
            }
            return null;
          })
          .filter((t): t is AISupportTurn => t !== null)
      );
      // Offer the guide back rather than forcing it open: they may have
      // reopened this conversation to read it, not to be walked through again.
      setLastWalkthrough(walkthroughFromDetail(detail));
      setSessionId(id);
      setUnreadSessions((prev) => prev.filter((sid) => sid !== id));
      setIsBusy(detail.session.status === "working");
      setPendingAction(detail.pending_actions[0] ?? null);
      // The session stays "escalated" for support's own list, but from the
      // therapist's side the wait is over once a supporter has replied.
      const answeredBySupport = [...detail.messages]
        .reverse()
        .find((m) => m.role === "support_reply" || m.role === "user");
      setIsEscalated(
        detail.session.status === "escalated" &&
          answeredBySupport?.role !== "support_reply"
      );
      // A conversation that already has a verdict, or that support closed, is
      // finished -- reopening it to read the answer must not produce a request
      // to close something that is closed.
      setOutcomeGiven(
        detail.session.status === "closed" || Boolean(detail.session.outcome)
      );
    } catch (error) {
      console.error("[AISupport] resume failed", error);
      setErrorMessage(null);
    }
  }, []);

  // At sign-in, ask about one conversation left open from a previous visit.
  //
  // Runs once per load of the authenticated app, which is what "at sign-in"
  // means in a single-page app. How often it may actually ask is the server's
  // decision, not this one: it hands back at most one conversation, no more
  // than CLOSE_REMINDER_MAX times each and never twice in a day, and records
  // the reminder as it does so. Keeping the count there rather than in
  // localStorage is the point -- a therapist signs in from more than one
  // machine, and per-browser memory would ask afresh on each.
  useEffect(() => {
    if (!token()) return;
    let cancelled = false;
    (async () => {
      const { session } = await apiService.takeAISupportCloseReminder(token());
      if (cancelled || !session) return;
      // Load the conversation so they can see what they are being asked about;
      // a verdict on a conversation you cannot read is a guess.
      await resumeSession(session.id);
      if (cancelled) return;
      setIsCloseAskOpen(true);
      setIsOpen(true);
    })();
    return () => { cancelled = true; };
    // Deliberately once per mount: the provider mounts with the authenticated
    // app, so this is the sign-in moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // open() runs before resumeSession is declared, so it reaches it by ref.
  resumeSessionRef.current = resumeSession;

  /**
   * Opening the assistant should resume where the therapist left off.
   *
   * With one conversation still open, reopen it. With several, show the list so
   * they can choose. Only a therapist with nothing outstanding gets a blank
   * composer -- otherwise the previous conversation is invisible unless they
   * think to go looking for it.
   */
  resetRef.current = reset;

  const open = useCallback(async () => {
    setIsOpen(true);

    // An unread answer is the reason they are opening it, so go there first --
    // and to the oldest one, which is the one that has been waiting longest.
    const waiting = unreadSessions[0];
    if (waiting !== undefined) {
      setUnreadSessions((prev) => prev.filter((id) => id !== waiting));
      if (waiting !== sessionId) {
        await resumeSessionRef.current?.(waiting);
        return;
      }
      return;
    }

    try {
      const res = await apiService.getMyAISupportSessions(token());
      setHistory(res.sessions);
      const unfinished = res.sessions.filter((s) => s.status !== "closed");

      if (!unfinished.length) {
        // Nothing outstanding: start fresh rather than reopening a finished
        // conversation, which is what the panel would otherwise still be
        // holding after the therapist marked one solved.
        resetRef.current?.();
        return;
      }
      if (sessionId && unfinished.some((s) => s.id === sessionId)) {
        return; // already on one of them
      }
      if (unfinished.length === 1) {
        await resumeSessionRef.current?.(unfinished[0].id);
        return;
      }
      setHistoryFilter("open");
      setIsHistoryOpen(true);
    } catch (error) {
      // A blank composer is a fine fallback; never block opening the panel.
      console.error("[AISupport] could not load open conversations", error);
    }
  }, [sessionId, unreadSessions]);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<AISupportContextValue>(
    () => ({
      isOpen, lang, sessionId, turns, isBusy, activity, pendingAction,
      isEscalated, errorMessage, awaitingVerdict, walkthrough,
      open, close, setLang, send, escalate, decidePendingAction, isDeciding,
      isClosable: sessionId !== null && !isEscalated && !outcomeGiven,
      isCloseAskOpen,
      openCloseAsk: () => setIsCloseAskOpen(true),
      dismissCloseAsk: () => setIsCloseAskOpen(false),
      submitVerdict, sendFeedbackNote,
      reset, setScreenSnapshot,
      dismissWalkthrough, lastWalkthrough, reopenWalkthrough,
      history, historyFilter, setHistoryFilter, isHistoryOpen, openHistory,
      closeHistory, resumeSession, unreadCount,
    }),
    [
      isOpen, lang, sessionId, turns, isBusy, activity, pendingAction,
      isEscalated, errorMessage, awaitingVerdict, walkthrough,
      open, close, setLang, send, escalate, decidePendingAction, isDeciding,
      outcomeGiven, isCloseAskOpen,
      submitVerdict, sendFeedbackNote,
      reset, setScreenSnapshot,
      dismissWalkthrough, lastWalkthrough, reopenWalkthrough,
      history, historyFilter, setHistoryFilter, isHistoryOpen, openHistory,
      closeHistory, resumeSession, unreadCount,
    ]
  );

  return <AISupportContext.Provider value={value}>{children}</AISupportContext.Provider>;
};
